import {
  Badge, Button, Card, DataTable, DateInput, ErrorState, Field, Input, LARGEST_PAGE, Notice, Permissions, Row, Select, Stack,
  Textarea, useApi, useI18n, usePermission, usePrintedFile,
  type BadgeTone, type YearFeeRow, type YearFeeStatus,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

/** The printed list of who still owes; one at a time, whichever year is shown. */
const BALANCES_FILE = 'balances';

const STATUSES: YearFeeStatus[] = ['owing', 'missing', 'paid'];

/** A balance this close to its deadline is shown as urgent. Display only; the reminders follow the tenant's schedule. */
const URGENT_DAYS = 7;

/**
 * Each child's total for an academic year, paid in installments by a deadline (Q3, Q44).
 *
 * The accountant's list of who still owes, nearest deadline first, then who has no total yet, then who has
 * paid. Setting or changing a total happens here, one child at a time; a change keeps its reason with the
 * total it replaced. The installments themselves are ordinary payments, recorded on the Payments tab.
 */
export function YearFees() {
  const api = useApi();
  const { t, tn, formatMoney, formatDate } = useI18n();
  const canSet = usePermission(Permissions.finance.issueInvoice);
  // The statement as a PDF is staff-only and needs its own key (Q41); the family reads the account on screen.
  const canPrint = usePermission(Permissions.finance.generateStatement);
  // Who still owes, printed: a report (FR-ADM-20), so `reports.view` beside `finance.view`.
  const canPrintBalances = usePermission(Permissions.tenant.viewReports);
  const printed = usePrintedFile();

  const [yearId, setYearId] = useState<number | ''>('');
  const [status, setStatus] = useState<YearFeeStatus | ''>('');
  const [editing, setEditing] = useState<YearFeeRow | null>(null);

  const years = useQuery({
    queryKey: ['academic-years', { pageSize: LARGEST_PAGE }],
    queryFn: () => api.academicYears.list({ pageSize: LARGEST_PAGE }),
  });

  const fees = useQuery({
    queryKey: ['finance', 'year-fees', yearId, status],
    queryFn: () => api.finance.yearFees({
      academicYearId: yearId === '' ? undefined : yearId,
      status: status === '' ? undefined : status,
      pageSize: LARGEST_PAGE,
    }),
  });

  const data = fees.data;

  return (
    <Stack>
      <Row>
        <Field label={t('yearFees.year')}>
          {(props) => (
            <Select {...props}
              value={yearId === '' ? data?.academicYearId ?? '' : yearId}
              onChange={(event) => { setYearId(Number(event.target.value)); setEditing(null); }}
            >
              {(years.data?.items ?? []).map((year) => (
                <option key={year.id} value={year.id}>{year.name}</option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('action.filter')}>
          {(props) => (
            <Select {...props} value={status} onChange={(event) => setStatus(event.target.value as YearFeeStatus | '')}>
              <option value="">{t('yearFees.filter.all')}</option>
              {STATUSES.map((value) => <option key={value} value={value}>{t(`yearFees.filter.${value}`)}</option>)}
            </Select>
          )}
        </Field>
      </Row>

      {canPrintBalances && data && (
        <Row>
          <Button
            variant="secondary"
            disabled={printed.busy === BALANCES_FILE}
            onClick={() => void printed.open(
              BALANCES_FILE, () => api.reports.balancesPdf({ academicYearId: data.academicYearId }))}
          >
            {t(printed.busy === BALANCES_FILE ? 'action.printing' : 'yearFees.printBalances')}
          </Button>
        </Row>
      )}

      {data && (data.defaultDeadline
        ? <Notice tone="info">{t('yearFees.deadline.default', { date: formatDate(data.defaultDeadline) })}</Notice>
        : <Notice tone="warning">{t('yearFees.deadline.none')}</Notice>)}

      {printed.error != null && <ErrorState error={printed.error} />}

      {editing && data && (
        <YearFeeForm
          key={editing.childId}
          row={editing}
          academicYearId={data.academicYearId}
          defaultDeadline={data.defaultDeadline}
          currency={data.currency}
          onDone={() => setEditing(null)}
        />
      )}

      <Card flush>
        <DataTable
          caption={t('yearFees.title')}
          rows={data?.items}
          rowKey={(row) => row.childId}
          isLoading={fees.isPending}
          error={fees.error}
          onRetry={() => void fees.refetch()}
          empty={t('yearFees.empty')}
          columns={[
            { key: 'child', header: t('child.one'), render: (r) => r.childName },
            { key: 'class', header: t('class.one'), render: (r) => r.className ?? '—' },
            {
              key: 'total',
              header: t('yearFees.total'),
              numeric: true,
              render: (r) => r.totalMinorUnits == null
                ? <Badge tone="neutral">{t('yearFees.notSet')}</Badge>
                : formatMoney(r.totalMinorUnits, data?.currency),
            },
            {
              key: 'paid',
              header: t('yearFees.paid'),
              numeric: true,
              render: (r) => formatMoney(r.paidMinorUnits, data?.currency),
            },
            {
              key: 'remaining',
              header: t('yearFees.remaining'),
              numeric: true,
              render: (r) => formatMoney(r.remainingMinorUnits, data?.currency),
            },
            {
              key: 'deadline',
              header: t('yearFees.deadline'),
              render: (r) => r.deadline == null ? '—' : (
                <span className="dg-stack dg-stack--tight">
                  <span>{formatDate(r.deadline)}</span>
                  {/* A deadline set apart for one child says why, so nobody "corrects" it back. */}
                  {r.deadlineReason && <span className="dg-muted dg-text-sm">{r.deadlineReason}</span>}
                </span>
              ),
            },
            {
              key: 'daysLeft',
              header: t('yearFees.daysLeft'),
              render: (r) => r.daysLeft == null || r.remainingMinorUnits === 0
                ? '—'
                : <Badge tone={urgency(r.daysLeft)}>{daysLabel(r.daysLeft)}</Badge>,
            },
            ...(canSet || canPrint ? [{
              key: 'action',
              header: t('years.actions'),
              render: (r: YearFeeRow) => {
                const fileKey = `statement-${r.childId}`;
                const busy = printed.busy === fileKey;
                return (
                  <Row>
                    {canSet && (
                      <Button variant="ghost" onClick={() => setEditing(r)}>
                        {t(r.totalMinorUnits == null ? 'yearFees.set' : 'yearFees.change')}
                      </Button>
                    )}
                    {canPrint && (
                      <Button
                        variant="ghost"
                        disabled={busy}
                        aria-label={t('yearFees.statementOf', { name: r.childName })}
                        onClick={() => void printed.open(fileKey, () => api.finance.statementPdf(r.childId))}
                      >
                        {t(busy ? 'action.printing' : 'yearFees.statement')}
                      </Button>
                    )}
                  </Row>
                );
              },
            }] : []),
          ]}
        />
      </Card>
    </Stack>
  );

  function daysLabel(days: number): string {
    if (days === 0) return t('yearFees.dueToday');
    return days > 0 ? tn('yearFees.daysLeft', days) : tn('yearFees.daysPast', -days);
  }
}

function urgency(daysLeft: number): BadgeTone {
  if (daysLeft < 0) return 'danger';
  return daysLeft <= URGENT_DAYS ? 'warning' : 'info';
}

/** Sets or changes one child's total. A change, or a deadline of their own, takes a reason. */
function YearFeeForm({ row, academicYearId, defaultDeadline, currency, onDone }: {
  row: YearFeeRow;
  academicYearId: number;
  defaultDeadline: string | null;
  currency: string;
  onDone: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t, toMinorUnits, fromMinorUnits } = useI18n();

  const [amount, setAmount] = useState(
    row.totalMinorUnits == null ? '' : String(fromMinorUnits(row.totalMinorUnits, currency)));
  const [deadline, setDeadline] = useState(row.deadline ?? defaultDeadline ?? '');
  const [reason, setReason] = useState('');

  const save = useMutation({
    mutationFn: () => api.finance.setYearFee(row.childId, {
      academicYearId,
      // Minor units from the same function the display divides by, never a hard-coded factor.
      amountMinorUnits: toMinorUnits(Number(amount), currency),
      // The year's own deadline is sent as none, so the server records no reason for it.
      deadline: deadline === '' || deadline === defaultDeadline ? null : deadline,
      reason: reason.trim() === '' ? null : reason.trim(),
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['finance'] });
      onDone();
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (Number(amount) > 0) save.mutate();
  };

  return (
    <Card title={t('yearFees.form.title', { name: row.childName })}>
      <form onSubmit={submit}>
        <Stack>
          {save.isError && <ErrorState error={save.error} />}

          <Field label={t('yearFees.form.amount')} required>
            {(props) => (
              <Input {...props}
                type="number"
                inputMode="decimal"
                min="0"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            )}
          </Field>

          <Field label={t('yearFees.form.deadline')} hint={t('yearFees.form.deadlineHint')}>
            {(props) => (
              <DateInput {...props} value={deadline} onChange={setDeadline} />
            )}
          </Field>

          <Field label={t('yearFees.form.reason')} hint={t('yearFees.form.reasonHint')}>
            {(props) => (
              <Textarea {...props} rows={2} value={reason} onChange={(event) => setReason(event.target.value)} />
            )}
          </Field>

          <Row>
            <Button type="submit" busy={save.isPending} busyLabel={t('action.saving')}>{t('action.save')}</Button>
            <Button type="button" variant="ghost" onClick={onDone}>{t('action.cancel')}</Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}
